import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import SkeletonLoader from './SkeletonLoader';
import { useNavigate, useSearchParams } from 'react-router-dom'; 
import { 
    FaChalkboardTeacher, FaEdit, FaTrash, FaPlus, FaBuilding, 
    FaLayerGroup, FaArrowRight, FaArrowLeft, FaEye, FaListOl, 
    FaChartLine, FaUserShield, FaSearch, FaClock, FaLink, FaInfoCircle 
} from 'react-icons/fa';

// Importazione Componenti Shared (Fondamentali per la nuova UI)
import { BaseModal, SuccessModal, ConfirmModal, ErrorModal, LoadingModal } from './SharedComponents/Modals';

const ClassesModule = () => {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // --- 1. STATI PER I DATI ---
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [tenants, setTenants] = useState([]); 

    // --- 2. STATI PER I MODALI (Visibilità) ---
    const [modals, setModals] = useState({
        form: false,          
        assignGroups: false,  
        manageTests: false,   
        permissions: false, 
        assignInstructors: false, // NUOVO: Modale Docenti
        success: false,       
        error: false,         
        confirm: false,       
        loading: false        
    });

    // --- 3. DATI TEMPORANEI ---
    const [currentClass, setCurrentClass] = useState(null);
    
    // Form Data
    const [formData, setFormData] = useState({ 
        id: null, name: '', course_name: '', description: '', 
        duration_hours: 0, target_tenant_id: '', tests_repeatable: false 
    });
    
    // Shuttle Box Data
    const [assignData, setAssignData] = useState({ available: [], assigned: [] });
    
    // Dati per Permessi
    const [permData, setPermData] = useState({ permissions: [], searchResults: [] });
    const [userSearch, setUserSearch] = useState('');
    const [selectedTenant, setSelectedTenant] = useState('all');
    
    // NUOVI DATI PER DOCENTI
    const [instructorsData, setInstructorsData] = useState({ available: [], assigned_ids: [] });
    const [instructorSearch, setInstructorSearch] = useState(''); // Filtro ricerca docenti

    // UI Helpers
    const [actionClass, setActionClass] = useState(null); 
    const [statusMsg, setStatusMsg] = useState({ title: '', msg: '' });

    // --- 4. FETCH DATI ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const tenantParam = auth.role === 'superadmin' ? '' : (auth.tenant_id || '');
            const res = await axios.get(`/api_classes.php?role=${auth.role}&user_id=${auth.user_id}&tenant_id=${tenantParam}`);
            
            if (res.data.success) {
                let data = res.data.data;
                const courseFilter = searchParams.get('course_id');
                if (courseFilter) {
                    data = data.filter(c => c.course_id == courseFilter);
                }
                setClasses(data);
            }
        } catch (error) { 
            console.error("Errore fetch classi", error); 
        } finally { 
            setLoading(false); 
        }
    }, [auth, searchParams]);

    useEffect(() => {
        if (auth.role === 'superadmin' && tenants.length === 0) {
            axios.get(`/api_tenants.php?role=${auth.role}`)
                .then(res => { if(res.data.success) setTenants(res.data.data); })
                .catch(err => console.error("Err tenants", err));
        }
    }, [auth.role, tenants.length]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- 5. LOGICHE CRUD ---
    const openModal = (cls = null) => {
        setActionClass(null); 
        if (cls) {
            setFormData({ 
                id: cls.id, 
                name: cls.name, 
                course_name: cls.course_name || '', 
                description: cls.description || '', 
                duration_hours: cls.duration_hours || 0,
                target_tenant_id: cls.tenant_id,
                tests_repeatable: cls.tests_repeatable == 1 
            });
            setCurrentClass(cls); 
        } else {
            setFormData({ 
                id: null, name: '', course_name: '', description: '', 
                duration_hours: 0, target_tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id,
                tests_repeatable: false
            });
            setCurrentClass(null);
        }
        setModals(m => ({ ...m, form: true }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setModals(m => ({ ...m, form: false, loading: true })); 
        
        try {
            const payload = { ...formData, user_id: auth.user_id, role: auth.role, tenant_id: auth.tenant_id };
            const res = await axios.post('/api_classes.php', payload);
            
            setModals(m => ({ ...m, loading: false }));
            
            if (res.data.success) {
                setStatusMsg({ title: 'Successo', msg: 'Operazione completata correttamente.' });
                setModals(m => ({ ...m, success: true }));
                fetchData();
            } else {
                setStatusMsg({ title: 'Attenzione', msg: res.data.message });
                setModals(m => ({ ...m, error: true }));
            }
        } catch (error) { 
            setModals(m => ({ ...m, loading: false }));
            setStatusMsg({ title: 'Errore Server', msg: error.response?.data?.message || 'Errore di connessione.' });
            setModals(m => ({ ...m, error: true }));
        }
    };

    const askDelete = (id) => {
        const cls = classes.find(c => c.id === id);
        setCurrentClass(cls);
        setActionClass(null);
        setModals(m => ({ ...m, confirm: true }));
    };

    const handleDelete = async () => {
        setModals(m => ({ ...m, confirm: false, loading: true }));
        try {
            await axios.delete(`/api_classes.php?id=${currentClass.id}&user_id=${auth.user_id}&role=${auth.role}&tenant_id=${auth.tenant_id}`);
            setModals(m => ({ ...m, loading: false }));
            fetchData();
        } catch (e) { 
            setModals(m => ({ ...m, loading: false }));
            setStatusMsg({ title: 'Errore', msg: 'Impossibile eliminare la classe.' });
            setModals(m => ({ ...m, error: true }));
        }
    };

    // --- 6. SHUTTLE BOX ---
    const moveItem = (item, direction) => {
        if (direction === 'add') {
            setAssignData(prev => ({
                available: prev.available.filter(i => i.id !== item.id),
                assigned: [...prev.assigned, item]
            }));
        } else {
            setAssignData(prev => ({
                assigned: prev.assigned.filter(i => i.id !== item.id),
                available: [...prev.available, item]
            }));
        }
    };

    const openAssignGroups = async (cls) => {
        setActionClass(null);
        setCurrentClass(cls);
        setAssignData({ available: [], assigned: [] });
        
        try {
            const res = await axios.get(`/api_class_assignments.php?class_id=${cls.id}&tenant_id=${cls.tenant_id}`);
            if (res.data.success) {
                const all = res.data.all_groups;
                const assignedIds = res.data.assigned_ids;
                setAssignData({
                    assigned: all.filter(g => assignedIds.includes(g.id)),
                    available: all.filter(g => !assignedIds.includes(g.id))
                });
                setModals(m => ({ ...m, assignGroups: true }));
            }
        } catch (e) { alert("Errore caricamento gruppi"); }
    };

    const saveGroups = async () => {
        try {
            await axios.post('/api_class_assignments.php', {
                class_id: currentClass.id,
                group_ids: assignData.assigned.map(g => g.id)
            });
            setModals(m => ({ ...m, assignGroups: false }));
            fetchData();
        } catch (e) { alert("Errore salvataggio"); }
    };

    const openTestsModal = async (cls) => {
        setActionClass(null);
        setCurrentClass(cls);
        setAssignData({ available: [], assigned: [] });
        try {
            const res = await axios.get(`/api_class_tests.php`, {
                params: { class_id: cls.id, tenant_id: cls.tenant_id, user_id: auth.user_id, role: auth.role }
            });
            if (res.data.success) {
                const allTests = res.data.available_tests; 
                const assignedIds = res.data.assigned_test_ids;
                setAssignData({
                    assigned: allTests.filter(t => assignedIds.includes(t.id)),
                    available: allTests.filter(t => !assignedIds.includes(t.id))
                });
                setModals(m => ({ ...m, manageTests: true }));
            }
        } catch (e) { alert("Impossibile caricare i test."); }
    };

    const saveTests = async () => {
        try {
            await axios.post('/api_class_tests.php', {
                class_id: currentClass.id,
                test_ids: assignData.assigned.map(t => t.id)
            });
            setModals(m => ({ ...m, manageTests: false }));
            fetchData(); 
        } catch (e) { alert("Errore salvataggio test."); }
    };

    // --- 7. DOCENTI (NUOVA FUNZIONE CON RICERCA) ---
    const openAssignInstructors = async (cls) => {
        setActionClass(null);
        setCurrentClass(cls);
        setInstructorSearch(''); // Reset ricerca
        try {
            // Chiede all'API tutti i docenti e chi è già assegnato (se esiste logica specifica)
            // Qui usiamo la logica permessi per identificare gli assegnati
            const resUsers = await axios.get(`/api_courses_utils.php?action=get_instructors&tenant_id=${cls.tenant_id}&course_id=0`);
            const resPerms = await axios.get(`/api_class_permissions.php?class_id=${cls.id}`);
            
            if (resUsers.data.success && resPerms.data.success) {
                const allInstructors = resUsers.data.users; // Lista Instructor/Admin/Professor
                const currentPerms = resPerms.data.permissions; // Chi ha permessi speciali
                const assignedIds = currentPerms.map(p => p.id);

                setInstructorsData({
                    available: allInstructors,
                    assigned_ids: assignedIds
                });
                setModals(m => ({ ...m, assignInstructors: true }));
            }
        } catch (e) { console.error(e); alert("Errore caricamento docenti"); }
    };

    const toggleInstructor = async (userId, isAssigned) => {
        try {
            if (isAssigned) {
                await axios.delete(`/api_class_permissions.php?class_id=${currentClass.id}&user_id=${userId}`);
                setInstructorsData(prev => ({ ...prev, assigned_ids: prev.assigned_ids.filter(id => id !== userId) }));
            } else {
                await axios.post('/api_class_permissions.php', {
                    class_id: currentClass.id, user_id: userId,
                    can_manage_users: 1, can_manage_tests: 1, can_view_reports: 1
                });
                setInstructorsData(prev => ({ ...prev, assigned_ids: [...prev.assigned_ids, userId] }));
            }
        } catch (e) { alert("Errore aggiornamento"); }
    };

    // --- 8. PERMESSI AVANZATI ---
    const openPermModal = async (cls) => {
        setActionClass(null);
        setCurrentClass(cls);
        setUserSearch('');
        loadPermissions(cls.id);
        setModals(m => ({ ...m, permissions: true }));
    };

    const loadPermissions = async (classId) => {
        try {
            const res = await axios.get(`/api_class_permissions.php?class_id=${classId}`);
            if (res.data.success) {
                setPermData({ permissions: res.data.permissions, searchResults: [] });
            }
        } catch (e) { console.error(e); }
    };

    const searchUsers = async (query) => {
        if (query.length < 3) return;
        try {
            const response = await axios.get(`/api_class_permissions.php`, {
                params: {
                    class_id: currentClass.id,
                    search: query,
                    filter_tenant_id: selectedTenant
                }
            });
            if (response.data.success) {
                setPermData(prev => ({ ...prev, searchResults: response.data.available }));
            }
        } catch (error) { console.error("Errore ricerca", error); }
    };

    const grantPermission = async (user, perms = {cmu:0, cmt:0, cvr:1}) => {
        try {
            await axios.post('/api_class_permissions.php', {
                class_id: currentClass.id, user_id: user.id,
                can_manage_users: perms.cmu ? 1 : 0, can_manage_tests: perms.cmt ? 1 : 0, can_view_reports: perms.cvr ? 1 : 0
            });
            loadPermissions(currentClass.id);
            setUserSearch('');
        } catch (e) { alert("Errore assegnazione permessi"); }
    };

    const revokePermission = async (userId) => {
        if(!window.confirm("Sei sicuro di voler revocare i permessi?")) return;
        try {
            await axios.delete(`/api_class_permissions.php?class_id=${currentClass.id}&user_id=${userId}`);
            loadPermissions(currentClass.id);
        } catch (e) { alert("Errore rimozione"); }
    };

    const filteredClasses = classes.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (c.course_name && c.course_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filtro per la lista docenti nel modale
    const filteredInstructors = instructorsData.available.filter(u => 
        u.name.toLowerCase().includes(instructorSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(instructorSearch.toLowerCase())
    );

    return (
        <div className="container-fluid fade-in">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="text-primary fw-bold m-0"><FaChalkboardTeacher className="me-2"/>Gestione Classi</h4>
                    <small className="text-muted d-none d-sm-block">Crea e organizza i percorsi formativi</small>
                </div>
                {auth.role !== 'user' && (
                    <button className="btn btn-success shadow-sm rounded-pill px-4" onClick={() => openModal()}>
                        <FaPlus className="me-2" /> Nuova Classe
                    </button>
                )}
            </div>

            {/* Barra Ricerca */}
            <div className="card shadow-sm mb-3 border-0 rounded-3">
                <div className="card-body p-2 d-flex align-items-center">
                    <FaSearch className="ms-2 text-muted" />
                    <input type="text" className="form-control border-0 shadow-none" placeholder="Cerca classe..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
            </div>

            {/* TABELLA DESKTOP */}
            <div className="card shadow-sm border-0 d-none d-md-block rounded-3 overflow-hidden">
                <div className="card-body p-0">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                {/* COLONNA 1: CLASSE + BADGE CORSI */}
                                <th className="ps-4" style={{width: '35%'}}>Classe & Corsi</th>
                                
                                <th>Configurazione</th>
                                <th>Visibilità Gruppi</th>
                                {auth.role === 'superadmin' && <th>Tenant</th>}
                                <th className="text-end pe-4">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? [...Array(3)].map((_, i) => <tr key={i}><td colSpan={auth.role === 'superadmin' ? 5 : 4} className="p-4"><SkeletonLoader /></td></tr>) : 
                             filteredClasses.length > 0 ? filteredClasses.map(c => (
                                <tr key={c.id}>
                                    <td className="ps-4">
                                        <div className="fw-bold text-primary fs-6">{c.name}</div>
                                        <small className="text-muted d-block text-truncate mb-1" style={{maxWidth: '300px'}}>{c.description}</small>
                                        
                                        {/* NUOVO BADGE: Cliccabile per aprire il modale Edit e vedere i dettagli */}
                                        {c.linked_courses ? (
                                            <div className="d-flex align-items-center mt-1" onClick={() => openModal(c)} style={{cursor: 'pointer'}} title="Clicca per dettagli corsi">
                                                <span className="badge bg-soft-primary text-primary rounded-pill border">
                                                    <FaLink className="me-1"/> Inclusa in Corsi
                                                </span>
                                            </div>
                                        ) : (
                                            c.course_name && <small className="text-muted"><FaInfoCircle className="me-1"/>{c.course_name}</small>
                                        )}
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="badge bg-secondary">{c.tests_count} Test</span>
                                            {c.tests_repeatable == 1 && <span className="badge bg-warning text-dark">Ripetibile</span>}
                                            {c.duration_hours > 0 && <span className="badge bg-info text-white"><FaClock className="me-1"/>{c.duration_hours}h</span>}
                                        </div>
                                    </td>
                                    <td>
                                        {c.assigned_groups ? (
                                            <span className="badge bg-info text-dark text-truncate" style={{maxWidth: '180px'}} title={c.assigned_groups}>
                                                <FaLayerGroup className="me-1"/> {c.assigned_groups}
                                            </span>
                                        ) : <span className="badge bg-light text-muted border">Privata</span>}
                                    </td>
                                    {auth.role === 'superadmin' && <td><small className="text-muted"><FaBuilding className="me-1"/>{c.tenant_name}</small></td>}
                                    <td className="text-end pe-4">
                                        <div className="btn-group gap-1">
                                            {auth.role !== 'user' && (
                                                <button className="btn btn-sm btn-outline-info" onClick={() => navigate(`/class-report/${c.id}`)} title="Analisi Classe"><FaChartLine /></button>
                                            )}
                                            
                                            {/* NUOVO BOTTONE: ASSEGNA DOCENTI */}
                                            {auth.role !== 'user' && (
                                                <button className="btn btn-sm btn-outline-dark" onClick={() => openAssignInstructors(c)} title="Assegna Docenti"><FaChalkboardTeacher /></button>
                                            )}
                                            
                                            {auth.role !== 'user' && (
                                                <button className="btn btn-sm btn-outline-warning" onClick={() => openPermModal(c)} title="Permessi Utenti"><FaUserShield /></button>
                                            )}
                                            
                                            {auth.role === 'user' ? (
                                                <button className="btn btn-sm btn-primary" onClick={() => openTestsModal(c)}>Vai ai Test</button>
                                            ) : (
                                                <>
                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openTestsModal(c)} title="Gestisci Test"><FaListOl /></button>
                                                    <button className="btn btn-sm btn-outline-success" onClick={() => openAssignGroups(c)} title="Visibilità Gruppi"><FaEye /></button>
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => openModal(c)} title="Modifica Info"><FaEdit /></button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => askDelete(c.id)} title="Elimina"><FaTrash /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : <tr><td colSpan={auth.role === 'superadmin' ? 5 : 4} className="text-center py-5 text-muted">Nessuna classe trovata.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* LISTA MOBILE (Adattata al nuovo stile) */}
            <div className="d-md-none">
                {loading ? <SkeletonLoader height="100px" count={3} /> :
                 filteredClasses.map(c => (
                    <div key={c.id} className="card mb-2 shadow-sm border-0" onClick={() => setActionClass(c)}>
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between mb-1">
                                <div className="fw-bold text-primary">{c.name}</div>
                                {c.linked_courses && <FaLink className="text-primary"/>}
                            </div>
                            <div className="small text-muted mb-2 text-truncate">{c.description}</div>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    {c.tests_repeatable == 1 && <span className="badge bg-warning text-dark me-1">R</span>}
                                    <span className="badge bg-secondary">{c.tests_count} Test</span>
                                </div>
                                <span className={`badge ${c.assigned_groups ? 'bg-info text-dark' : 'bg-light text-muted border'}`}>
                                    {c.assigned_groups ? 'Visibile' : 'Nascosta'}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* MOBILE ACTION MENU */}
            {actionClass && (
                <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060}} onClick={() => setActionClass(null)}>
                    <div className="modal-dialog modal-dialog-centered px-4">
                        <div className="modal-content shadow border-0 overflow-hidden">
                            <div className="modal-header bg-light pb-2">
                                <h6 className="modal-title fw-bold text-truncate w-100">{actionClass.name}</h6>
                            </div>
                            <div className="modal-body d-grid gap-2 p-3">
                                {auth.role === 'user' ? (
                                    <button className="btn btn-primary py-2" onClick={() => openTestsModal(actionClass)}>Vai ai Test</button>
                                ) : (
                                    <>
                                        <button className="btn btn-outline-info text-start" onClick={() => { setActionClass(null); navigate(`/class-report/${actionClass.id}`); }}><FaChartLine className="me-2"/> Report Analisi</button>
                                        <button className="btn btn-outline-dark text-start" onClick={() => openAssignInstructors(actionClass)}><FaChalkboardTeacher className="me-2"/> Assegna Docenti</button>
                                        <button className="btn btn-outline-warning text-start" onClick={() => openPermModal(actionClass)}><FaUserShield className="me-2"/> Permessi Utenti</button>
                                        <button className="btn btn-outline-secondary text-start" onClick={() => openTestsModal(actionClass)}><FaListOl className="me-2"/> Gestisci Test</button>
                                        <button className="btn btn-outline-success text-start" onClick={() => openAssignGroups(actionClass)}><FaEye className="me-2"/> Visibilità Gruppi</button>
                                        <button className="btn btn-outline-primary text-start" onClick={() => openModal(actionClass)}><FaEdit className="me-2"/> Modifica Info</button>
                                        <button className="btn btn-outline-danger text-start" onClick={() => askDelete(actionClass.id)}><FaTrash className="me-2"/> Elimina</button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODALE 1: CREATE / EDIT --- */}
            <BaseModal show={modals.form} onClose={() => setModals(m => ({...m, form: false}))} title={formData.id ? 'Modifica Classe' : 'Nuova Classe'}>
                <div className="text-start p-2">
                    
                    {/* INFO CORSI COLLEGATI */}
                    {formData.id && currentClass && currentClass.linked_courses && (
                        <div className="alert alert-primary py-2 small mb-3">
                            <div className="d-flex align-items-start">
                                <FaLink className="me-2 mt-1"/>
                                <div>
                                    <strong>Corsi Collegati:</strong>
                                    <div className="mt-1" style={{lineHeight:'1.4'}}>{currentClass.linked_courses}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {auth.role === 'superadmin' && (
                        <div className="mb-3">
                            <label className="form-label small fw-bold text-muted">Azienda (Tenant)</label>
                            <select className="form-select" value={formData.target_tenant_id} onChange={e => setFormData({...formData, target_tenant_id: e.target.value})} disabled={!!formData.id}>
                                <option value="">Seleziona...</option>
                                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-md-8 mb-3">
                            <label className="form-label small fw-bold text-muted">Nome Classe</label>
                            <input type="text" className="form-control" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label small fw-bold text-muted">Ore Corso</label>
                            <input type="number" className="form-control" value={formData.duration_hours} onChange={e => setFormData({...formData, duration_hours: e.target.value})} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">Corso di Afferenza (Testo Manuale)</label>
                        <input type="text" className="form-control" placeholder="Es. Lettere Anno 1 (Opzionale)" value={formData.course_name} onChange={e => setFormData({...formData, course_name: e.target.value})} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">Descrizione</label>
                        <textarea className="form-control" rows="2" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
                    </div>
                    <div className="form-check form-switch mb-2">
                        <input className="form-check-input" type="checkbox" id="repCheck" checked={formData.tests_repeatable} onChange={e => setFormData({...formData, tests_repeatable: e.target.checked})} />
                        <label className="form-check-label" htmlFor="repCheck">Test ripetibili dagli utenti</label>
                    </div>
                    <div className="text-end mt-4">
                        <button className="btn btn-light rounded-pill px-4 me-2" onClick={() => setModals(m => ({...m, form: false}))}>Annulla</button>
                        <button className="btn btn-primary rounded-pill px-4 fw-bold" onClick={handleSubmit}>Salva Classe</button>
                    </div>
                </div>
            </BaseModal>

            {/* --- NUOVO MODALE: ASSEGNAZIONE DOCENTI --- */}
            <BaseModal show={modals.assignInstructors} onClose={() => setModals(m => ({...m, assignInstructors: false}))} title="Gestione Docenti Classe" size="medium">
                <div className="text-start">
                    <p className="small text-muted mb-3">
                        Seleziona i docenti (Instructor, Professor, Admin) che devono avere accesso completo a questa classe.
                    </p>
                    
                    {/* FILTRO RICERCA */}
                    <div className="mb-3">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white"><FaSearch className="text-muted"/></span>
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Cerca docente..." 
                                value={instructorSearch}
                                onChange={(e) => setInstructorSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="list-group border bg-white overflow-auto" style={{maxHeight:'300px'}}>
                        {filteredInstructors.length > 0 ? filteredInstructors.map(u => {
                            const isAssigned = instructorsData.assigned_ids.includes(u.id);
                            return (
                                <label key={u.id} className={`list-group-item d-flex justify-content-between align-items-center ${isAssigned ? 'bg-light' : ''}`} style={{cursor:'pointer'}}>
                                    <div className="d-flex align-items-center">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input me-3" 
                                            checked={isAssigned} 
                                            onChange={() => toggleInstructor(u.id, isAssigned)}
                                        />
                                        <div>
                                            <div className="fw-bold d-flex align-items-center">
                                                {u.name} 
                                                <span className={`badge ms-2 ${u.role==='admin' ? 'bg-danger' : (u.role==='professor' ? 'bg-info' : 'bg-secondary')}`} style={{fontSize:'0.65em'}}>{u.role}</span>
                                            </div>
                                            <div className="small text-muted">{u.email}</div>
                                        </div>
                                    </div>
                                    {isAssigned && <FaUserShield className="text-success"/>}
                                </label>
                            );
                        }) : (
                            <div className="text-center p-3 text-muted">
                                {instructorsData.available.length === 0 ? "Nessun docente trovato." : "Nessun risultato."}
                            </div>
                        )}
                    </div>
                    <div className="mt-3 text-end">
                        <button className="btn btn-primary rounded-pill px-4" onClick={() => setModals(m => ({...m, assignInstructors: false}))}>Chiudi</button>
                    </div>
                </div>
            </BaseModal>

            {/* --- MODALE SHUTTLE BOX --- */}
            <BaseModal show={modals.assignGroups || modals.manageTests} size="large" title={modals.manageTests ? "Gestione Contenuto Test" : "Visibilità Gruppi"} onClose={() => setModals(m => ({...m, assignGroups: false, manageTests: false}))} footer={<button className="btn btn-primary rounded-pill px-5 fw-bold" onClick={modals.manageTests ? saveTests : saveGroups}>Salva Configurazione</button>}>
                <div className="row g-3 text-start">
                    <div className="col-md-6"><div className="card h-100 border-0 bg-light shadow-sm"><div className="card-header bg-transparent border-0 small fw-bold text-muted">{modals.manageTests ? 'Disponibili' : 'Disponibili'}</div><div className="list-group list-group-flush overflow-auto" style={{height: '350px'}}>{assignData.available.map(item => (<button key={item.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => moveItem(item, 'add')}><span className="text-truncate">{item.name}</span> <FaArrowRight className="text-primary" /></button>))}</div></div></div>
                    <div className="col-md-6"><div className="card h-100 border-0 shadow-sm" style={{backgroundColor: '#f8faff'}}><div className="card-header bg-transparent border-0 small fw-bold text-primary">{modals.manageTests ? 'Inclusi (In Ordine)' : 'Abilitati'}</div><div className="list-group list-group-flush overflow-auto" style={{height: '350px'}}>{assignData.assigned.map((item, idx) => (<button key={item.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => moveItem(item, 'remove')}><div className="text-truncate">{modals.manageTests && <span className="badge bg-white text-dark border me-2">{idx + 1}</span>}{item.name}</div> <FaTrash className="text-danger small" /></button>))}</div></div></div>
                </div>
            </BaseModal>

            {/* --- MODALE PERMESSI (Avanzato) --- */}
            <BaseModal show={modals.permissions} size="large" title={`Permessi Speciali: ${currentClass?.name}`} onClose={() => setModals(m => ({...m, permissions: false}))} hideButtons={true}>
                <div className="text-start">
                    <div className="mb-4 position-relative bg-light p-3 rounded">
                        {auth.role === 'superadmin' && (<div className="mb-2"><select className="form-select form-select-sm" value={selectedTenant} onChange={e => { setSelectedTenant(e.target.value); setPermData(prev => ({ ...prev, searchResults: [] })); }}><option value="all">Tutti i Tenant</option>{tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>)}
                        <label className="small fw-bold text-muted">Aggiungi Utente (Qualsiasi Ruolo):</label>
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0"><FaSearch className="text-muted"/></span>
                            <input type="text" className="form-control border-start-0" placeholder="Cerca..." value={userSearch} onChange={e => { setUserSearch(e.target.value); searchUsers(e.target.value); }} />
                        </div>
                        {permData.searchResults.length > 0 && <div className="list-group position-absolute w-100 shadow-lg" style={{zIndex: 1100}}>{permData.searchResults.map(u => (<button key={u.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => grantPermission(u)}><div><strong>{u.name}</strong> <small>({u.email})</small></div><span className="badge bg-primary">Aggiungi</span></button>))}</div>}
                    </div>
                    <div className="table-responsive"><table className="table table-sm align-middle"><thead><tr className="small text-muted"><th>Utente</th><th className="text-center">Utenti</th><th className="text-center">Test</th><th className="text-center">Report</th><th></th></tr></thead><tbody>{permData.permissions.map(p => (<tr key={p.id}><td><div className="fw-bold">{p.name}</div><small>{p.email}</small></td><td className="text-center"><input type="checkbox" checked={p.can_manage_users == 1} onChange={e => grantPermission({id: p.id}, {cmu: e.target.checked, cmt: p.can_manage_tests, cvr: p.can_view_reports})} /></td><td className="text-center"><input type="checkbox" checked={p.can_manage_tests == 1} onChange={e => grantPermission({id: p.id}, {cmu: p.can_manage_users, cmt: e.target.checked, cvr: p.can_view_reports})} /></td><td className="text-center"><input type="checkbox" checked={p.can_view_reports == 1} onChange={e => grantPermission({id: p.id}, {cmu: p.can_manage_users, cmt: p.can_manage_tests, cvr: e.target.checked})} /></td><td className="text-end"><button className="btn btn-sm text-danger" onClick={() => revokePermission(p.id)}><FaTrash/></button></td></tr>))}</tbody></table></div>
                </div>
            </BaseModal>

            <SuccessModal show={modals.success} title={statusMsg.title} message={statusMsg.msg} onClose={() => setModals(m => ({...m, success: false}))} />
            <ConfirmModal show={modals.confirm} title="Conferma Eliminazione" message={`Eliminare la classe "${currentClass?.name}"?`} onConfirm={handleDelete} onClose={() => setModals(m => ({...m, confirm: false}))} isDestructive={true} />
            <LoadingModal show={modals.loading} message="Elaborazione..." />
            <ErrorModal show={modals.error} title={statusMsg.title} message={statusMsg.msg} onClose={() => setModals(m => ({...m, error: false}))} />
        </div>
    );
};

export default ClassesModule;